import { z } from "@/zod";
import { ulid, dateTime, phoneNumber } from "./commonFields";
import { permission } from "./permission";
import { AVAILABLE_LANGUAGES, AvailableLanguage } from "@/i18n";
import i18next from "i18next";

export const PAYROLL_COMMENT_MAX_LENGTH = 500;

export const SYSTEM_ROLE_CODE = {
  CONTRACTOR: "contractor",
  VICE_STORE_COORDINATOR: "vice_store_coordinator",
  STORE_COORDINATOR: "store_coordinator",
  SUPERVISOR: "supervisor",
  REGIONAL_REPRESENTATIVE: "regional_representative",
  REGIONAL_DIRECTOR: "regional_director",
  FIELD_ACCOUNT_MANAGER: "field_account_manager",
  HR_DEPARTMENT: "hr_department",
  FINANCE_ACCOUNTING: "finance_accounting",
  CKM_LEAD: "ckm_lead",
  LEASING_OUTSOURCING: "leasing_outsourcing",
  KEY_ACCOUNT_MANAGER: "key_account_manager",
  CUSTOMER_SERVICE_DIRECTOR: "customer_service_director",
  CONTROL_AND_REPORTS: "control_and_reports",
  OPERATIONAL_DIRECTOR: "operational_director",
  SYSTEM_ADMINISTRATOR: "system_administrator",
};

const authenticatedAs = z.object({
  id: ulid,
  cognito_id: z.string(),
  email: z.string().email(),
  first_name: z.string(),
  last_name: z.string(),
  full_name: z.string(),
  system_role_code: z.string(),
  system_role_name: z.string(),
});

export const user = z.object({
  id: ulid,
  ref_number: z.number().optional(),
  cognito_id: z.string(),
  contractor_id: ulid.optional(),
  email: z.string().email(),
  first_name: z.string(),
  last_name: z.string(),
  full_name: z.string(),
  phone_number: phoneNumber,
  permissions: z.array(permission),
  preferred_language_iso_code: z.string(),
  system_role_code: z.nativeEnum(SYSTEM_ROLE_CODE),
  system_role_description: z.string(),
  system_role_id: ulid,
  system_role_name: z.string(),
  is_active: z.boolean(),
  tax_status: z.string().optional(),
  payroll_comment: z.string().max(PAYROLL_COMMENT_MAX_LENGTH).optional(),
  contractor_is_active: z.boolean().optional(),
  deleted_at: dateTime.optional(),
  authenticated_as: authenticatedAs.optional(),
});

export const readUser = user.readonly();
export const displayUser = user.omit({ permissions: true }).readonly();

export const writeUser = z
  .object({
    email: z.string().email(),
    first_name: z.string().min(1).trim(),
    last_name: z.string().min(1).trim(),
    phone_number: phoneNumber,
    system_role_id: ulid,
    preferred_language_iso_code: z.enum(
      AVAILABLE_LANGUAGES as [AvailableLanguage, ...AvailableLanguage[]],
    ),
    register_as_contractor: z.boolean(),
    tax_status: z.string().optional(),
    payroll_comment: z.string().max(PAYROLL_COMMENT_MAX_LENGTH).optional(),
  })
  .superRefine(({ register_as_contractor, tax_status }, ctx) => {
    if (register_as_contractor && !tax_status) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18next.t("users.validation.tax_status"),
        path: ["tax_status"],
      });
    }
  });

export const createUser = writeUser;

export const updateUser = writeUser;
